import { STUDIO_FROM } from "../vogelkop.ts";

export type MessageChannel = "sms" | "email";

export type OutboundMessage = {
  channel: MessageChannel;
  to: string;
  body: string;
  subject?: string | null;
  from?: string | null;
  replyTo?: string | null;
};

export type DeliveryResult = {
  provider: string;
  providerMessageId: string | null;
  status: "sent" | "queued" | "failed" | "delivered";
  sentAt: string | null;
  deliveredAt?: string | null;
  error?: string;
};

export interface MessagingDriver {
  name: string;
  send(message: OutboundMessage): Promise<DeliveryResult>;
}

export type MessagingEnv = {
  smsDriver?: string | null;
  emailDriver?: string | null;
  telnyxApiKey?: string | null;
  telnyxFromNumber?: string | null;
  resendApiKey?: string | null;
  resendFrom?: string | null;
};

export class MessagingNotConfiguredError extends Error {}

type FetchLike = typeof fetch;

const messageId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `mock_${Math.random().toString(36).slice(2, 10)}`;

export const mockDriver: MessagingDriver = {
  name: "mock",
  async send(message) {
    if (!message.to?.trim() || !message.body?.trim()) {
      return {
        provider: "mock",
        providerMessageId: null,
        status: "failed",
        sentAt: null,
        error: "A recipient and a message are required",
      };
    }
    const now = new Date().toISOString();
    return {
      provider: "mock",
      providerMessageId: messageId(),
      status: "delivered",
      sentAt: now,
      deliveredAt: now,
    };
  },
};

export function createTelnyxDriver(
  env: MessagingEnv,
  fetchImpl: FetchLike = fetch,
): MessagingDriver {
  return {
    name: "telnyx",
    async send(message) {
      if (!env.telnyxApiKey || !env.telnyxFromNumber) {
        throw new MessagingNotConfiguredError(
          "Telnyx is not configured. Set TELNYX_API_KEY and TELNYX_FROM_NUMBER.",
        );
      }
      const response = await fetchImpl("https://api.telnyx.com/v2/messages", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${env.telnyxApiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          from: message.from || env.telnyxFromNumber,
          to: message.to,
          text: message.body,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail =
          payload?.errors?.[0]?.detail ||
          payload?.errors?.[0]?.title ||
          response.statusText;
        return {
          provider: "telnyx",
          providerMessageId: null,
          status: "failed",
          sentAt: null,
          error: detail || "Telnyx rejected the message",
        };
      }
      return {
        provider: "telnyx",
        providerMessageId: payload?.data?.id ?? null,
        status: "queued",
        sentAt: null,
      };
    },
  };
}

export function createResendDriver(
  env: MessagingEnv,
  fetchImpl: FetchLike = fetch,
): MessagingDriver {
  return {
    name: "resend",
    async send(message) {
      if (!env.resendApiKey || !env.resendFrom) {
        throw new MessagingNotConfiguredError(
          "Resend is not configured. Set RESEND_API_KEY and RESEND_FROM.",
        );
      }
      const from = env.resendFrom?.trim() || STUDIO_FROM;
      const response = await fetchImpl("https://api.resend.com/emails", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${env.resendApiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          from,
          to: [message.to],
          subject: message.subject || "Message from Vogelkop",
          text: message.body,
          reply_to: message.replyTo || undefined,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          provider: "resend",
          providerMessageId: null,
          status: "failed",
          sentAt: null,
          error:
            payload?.message ||
            response.statusText ||
            "Resend rejected the message",
        };
      }
      return {
        provider: "resend",
        providerMessageId: payload?.id ?? null,
        status: "sent",
        sentAt: new Date().toISOString(),
      };
    },
  };
}

/**
 * Email uses Resend when RESEND_API_KEY and RESEND_FROM are set.
 * MESSAGING_EMAIL_DRIVER=mock keeps the mock driver even if those are set.
 * The browser always uses {@link mockDriver} so the key stays on the server.
 * SMS stays unused unless MESSAGING_SMS_DRIVER=telnyx.
 */
export function driverFor(
  channel: MessageChannel,
  env: MessagingEnv = {},
  fetchImpl: FetchLike = fetch,
): MessagingDriver {
  if (channel === "sms" && env.smsDriver === "telnyx") {
    return createTelnyxDriver(env, fetchImpl);
  }
  if (channel === "email" && env.emailDriver !== "mock") {
    const resendFrom = env.resendFrom?.trim() || undefined;
    const configured =
      env.emailDriver === "resend" ||
      (Boolean(env.resendApiKey) && Boolean(resendFrom));
    if (configured) {
      return createResendDriver(
        { ...env, resendFrom: resendFrom || STUDIO_FROM },
        fetchImpl,
      );
    }
  }
  return mockDriver;
}
